import { useId } from 'react'
import { useHitTarget } from '../responsive/hitTarget'
import { CatalogueIconImg } from './CatalogueIconImg'
import type { CatalogueIcon } from './useIconCatalogue'
import './iconCatalogue.css'

/**
 * Pick one icon from the catalogue — #447. Presentational: the icons arrive
 * as props, and `IconCataloguePicker` is what fetches them.
 *
 * `platform/primitives/IconGrid` is the sibling this does NOT reuse, and the
 * split is the point. `IconGrid` takes the 88 bundled design slugs and draws
 * each through `BY_SLUG`. The catalogue's entries are objects that carry
 * their own `label` and `icon_url`, and both the category AND the merchant
 * write paths 422 any slug that is not one of them (backend #401/#403), so a
 * grid that could be handed a bundled slug would be a grid that could produce
 * a 422. Since #461 no form offers the bundled set for a server-validated
 * icon at all.
 *
 * **Same mechanism as `IconGrid`:** a real
 * `<input type="radio">` per cell, stretched invisibly over it (so the input
 * itself is the measured hit target), grouped by a `useId` name so two
 * instances never share native exclusivity, and a `role="radiogroup"` with
 * the caller's `label` as its accessible name.
 *
 * **Named by the catalogue's own `label`** ("Guitar"), never the slug.
 * `title` gives a sighted pointer user the same name.
 *
 * **The `<img>` is decorative inside a named control** — `alt=""`, since the
 * radio's `aria-label` already names it. Untinted, by the user's decision: see
 * `CategoryIcon.tsx`. In dark theme `iconCatalogue.css` inverts it
 * (`--catalogue-icon-filter`, #445) so the black glyph does not vanish on
 * this cell's own `--glass-3` fill — reversed back to `none` on the SELECTED
 * cell, whose `--accent` fill is a light lime in both themes.
 *
 * Selection is `aria-checked` (native), a fill AND a ring
 * (`iconCatalogue.css`), never colour alone (M0-R11).
 */
export function CatalogueIconGrid({
  icons,
  value,
  onChange,
  label,
}: {
  icons: CatalogueIcon[]
  /** The selected slug, or null. */
  value: string | null
  onChange: (icon: CatalogueIcon) => void
  label: string
}) {
  const name = useId()
  const target = useHitTarget('page')

  return (
    <div className="icon-catalogue-grid" role="radiogroup" aria-label={label}>
      {icons.map((icon) => (
        // `slug` is the catalogue's primary key, so it is unique per page and
        // across pages (paging is stable: ties break on slug).
        <label
          key={icon.slug}
          className="icon-catalogue-grid__cell"
          title={icon.label}
          style={{ minHeight: target, minWidth: target }}
        >
          <input
            type="radio"
            name={name}
            className="icon-catalogue__input"
            checked={value === icon.slug}
            onChange={() => onChange(icon)}
            aria-label={icon.label}
          />
          <CatalogueIconImg src={icon.icon_url} size={24} className="icon-catalogue-grid__img" />
        </label>
      ))}
    </div>
  )
}
